import {
  GetResultConceptTotalsDocument,
  GetResultConceptTotalsQuery,
} from 'generated/resources/graphql'
import { ResultConceptItemTableRowFragment } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

export type PayrollResultConceptTotal =
  GetResultConceptTotalsQuery['payroll']['currentPeriodResultConceptTotals'][number]

export const useResultConceptTotals = (
  currentPolicyPeriodId?: number,
  otherPolicyPeriodId?: number
) => {
  const { data, error, refetch, loading } = useQuery(GetResultConceptTotalsDocument, {
    variables: {
      currentPolicyPeriodId,
      otherPolicyPeriodId,
      latestByEmployee: true,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !currentPolicyPeriodId,
  })

  const currentPeriodResultConceptTotals = data?.payroll.currentPeriodResultConceptTotals ?? []
  const otherPeriodResultConceptTotals = data?.payroll.otherPeriodResultConceptTotals ?? []

  return {
    refetch,
    fetching: loading,
    currentPeriodResultConceptTotals,
    otherPeriodResultConceptTotals,
    hasCurrentPeriodResultConceptTotals: currentPeriodResultConceptTotals.length > 0,
    error,
  }
}

export const groupByConceptName = (
  resultConceptTotals: PayrollResultConceptTotal[],
  mappedResultConcepts: ResultConceptItemTableRowFragment['concept'][]
): { [conceptName: string]: PayrollResultConceptTotal } => {
  return resultConceptTotals.reduce((groupedByConceptName, resultConceptTotal) => {
    const concept = getConceptByConceptId(resultConceptTotal, mappedResultConcepts)

    if (concept) {
      return {
        ...groupedByConceptName,
        [concept.name]: resultConceptTotal,
      }
    }
    return groupedByConceptName
  }, {} as { [conceptName: string]: PayrollResultConceptTotal })
}

export const getConceptTotalLabel = (
  resultConceptTotal: PayrollResultConceptTotal,
  mappedResultConcepts: ResultConceptItemTableRowFragment['concept'][]
): string => {
  const concept = getConceptByConceptId(resultConceptTotal, mappedResultConcepts)

  if (!concept) return ''
  if (!concept.default) {
    return concept.name
  }

  return concept.label
}

export const getConceptByConceptId = (
  resultConceptTotal: PayrollResultConceptTotal,
  mappedResultConcepts: ResultConceptItemTableRowFragment['concept'][]
): ResultConceptItemTableRowFragment['concept'] | undefined => {
  const concept = mappedResultConcepts.find(
    (mappedConcept) => mappedConcept.id === resultConceptTotal.conceptId
  )
  return concept
}
